import { REGISTER_MUTATION, USER_EXISTS_QUERY } from "../../User/User.querys";
import { useContext, useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
	useFormulario,
	valueFormDataType,
} from "../../../../shared-components/GlobalHooks/web/useFormulario";
import { PropComponentMode } from "../../Property/PropertyInterfaces";
import { gql } from "@apollo/client/core";
import { useUser } from "../../User/User.hook";
import { isTransactional } from "../../../Utils/Functions";
import { useCreateSearchAlert } from "shared-components/Components/SearchAlert/CreateSearchAlert/hook";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import usePropertyData from "src/hooks/propertyData/usePropertyData";
import { trackNpsLeadSent } from "shared-components/Components/NpsSurveyCard/npsSurvey.service";

interface useContactParams {
	id: number;
	onComplete: (formType: number) => void;
	type: string;
	isProject?: boolean;
	source?: number;
	utm?: any;
	env?: "development" | "production";
}

export enum VisitType {
	INPERSON = "INPERSON",
	VIRTUAL = "VIRTUAL",
}

enum PropEntityType {
	PROPERTY = "PROPERTY",
	PROJECT = "PROJECT",
}

interface FormData {
	email: string;
	phone: number | string;
	name: string;
	message?: string;
	financing_information?: boolean | undefined;
	date?: string;
	visit_type?: VisitType;
	isFromPaidTraffic?: string;
	typeForm?: number;
	busca_por_ti?: boolean;
	is_terms_and_conditions_accepted?: boolean;
	isProject?: boolean;
	projectId?: string;
}

interface ContactInput {
	property_id: string;
	source: number;
	type: PropEntityType;
	financing_information: boolean;
	message?: string;
	name?: string;
	email?: string;
	phone?: string | number;
	visit_type?: string;
	date?: string;
	utm_args?: UtmArgs;
	isFromPaidTraffic?: string;
	is_terms_and_conditions_accepted?: boolean;
	busca_por_ti?: boolean
}

export interface UtmArgs {
	utm_content?: string | string[];
	utm_source?: string | string[];
	utm_medium?: string | string[];
	utm_campaign?: string | string[];
}

const SEND_LEAD_MUTATION = gql`
	mutation sendLead($input: LeadInput!) {
		sendLead(input: $input) {
			id
		}
	}
`;

const SCHEDULE_VISIT_MUTATION = gql`
	mutation scheduleVisit($input: ScheduleInput!) {
		scheduleVisit(input: $input) {
			id
		}
	}
`;

const UPDATE_USER_MUTATION = gql`
	mutation updateUser($name: String, $phone: String) {
		updateUser(name: $name, phone: $phone) {
			id
		}
	}
`;

export const useContact = ({
	id,
	onComplete,
	type,
	isProject = false,
	source = 0,
	utm = null,
	env,
}: useContactParams) => {
	const { site_name } = useContext(ConfigStateContext);
	const { listing } = usePropertyData();
	const { searchAlert } = useCreateSearchAlert();
	const { user, isLoggedIn } = useUser();
	const [formData, setFormData] = useState<FormData | null>(null);
	const [formComplete, setFormComplete] = useState(false);
	const [leadID, setLeadID] = useState<string>("");
	const [IDform, setIDform] = useState(null);
	const { sendFormulario } = useFormulario({ IDform, env });
	const [isSendedConsultation, setIsSendedConsultation] = useState({
		consultationForm: false,
		sendConsultation: false,
		coordinateVisit: false,
		askMoreInfo: false,
	});

	const makeExtra = values => {
		let res = values["financing_information"]
			? "Requiere información de financiación" + "."
			: "No requiere información de financiación" + ".";
		if (values["message"]) res += " " + "Mensaje" + ": " + values["message"] + ".";
		if (values["date"]) res += " " + "Fecha de agenda" + ": " + values["date"] + ".";
		return res;
	};

	const [contactMutation, { loading: contactLoading, data: contactData }] = useMutation(
		type == "lead" ? SEND_LEAD_MUTATION : SCHEDULE_VISIT_MUTATION,
		{
			onCompleted: res => {
				setLeadID(res?.sendLead?.id ?? "");
				setFormComplete(true);
			},
			onError: error => {
				console.error(error);
			},
		}
	);
	const [
		registerMutation,
		{ loading: registerLoading, data: dataRegisterMutation },
	] = useMutation(REGISTER_MUTATION);
	const [updateUserMutation, { loading: updateLoading }] = useMutation(UPDATE_USER_MUTATION);

	const [
		userExistsQuery,
		{ loading: userExistsLoading, data: dataUserExist, error },
	] = useLazyQuery(USER_EXISTS_QUERY, {
		fetchPolicy: "network-only",
		onError: error => {
			if (error.message.includes("no existe")) {
				registerMutation({
					variables: {
						email: formData.email,
						name: formData.name,
					},
				})
					.then(({ data }) => {
						updateUser({ name: formData.name, phone: formData.phone }).then(() => {
							doContactMutation().then(() => user.refetch());
						});
					})
					.catch(error => {
						console.error(error);
					});
			}
		},
	});

	const [
		userExistsCustomQuery,
		{ data: dataUserExistCustom, loading: userExistsCustomLoading },
	] = useLazyQuery(USER_EXISTS_QUERY, {
		fetchPolicy: "network-only",
		// onError: error => {
		// 	if (error.message.includes("no existe")) {
		// 		registerMutation({
		// 			variables: {
		// 				email: formData.email,
		// 				name: formData.name,
		// 			},
		// 		})
		// 			.then(({ data }) => {
		// 				updateUser({ name: formData.name, phone: formData.phone }).then(() => {
		// 					doContactMutation().then(() => user.refetch());
		// 				});
		// 			})
		// 			.catch(error => {
		// 				console.error(error);
		// 			});
		// 	}
		// },
	});

	useEffect(() => {
		if (formData) {
			if (isLoggedIn) {
				updateUser({ name: formData.name, phone: formData.phone }).then(data => {
					doContactMutation().then(() => user.refetch());
				});
			} else {
				userExistsQuery({ variables: { email: formData.email } });
			}
		}
	}, [formData]);

	useEffect(() => {
		if (IDform) {
			sendTransactionalContact()
				.then(_ => {
					if (_.err) {
						throw new Error(_.err);
					} else {
						if (type === "lead") trackNpsLeadSent();
						if (onComplete) onComplete(0);
					}
					setIDform(null);
				})
				.catch(error => {
					console.error(error);
					setIDform(null);
				});
		}
	}, [IDform]);

	const updateUser = ({ name, phone }) => {
		return updateUserMutation({ variables: { name, phone } });
	};

	const doContactMutation = (formType: number = 0, withoutLogin = false, extraFields: FormData = null) => {
		let property_id: string;
		let propertyType: PropEntityType;
		property_id = listing?.id ?? id.toString();
		propertyType = isProject ? PropEntityType.PROJECT : PropEntityType.PROPERTY;

		let contactInput: ContactInput = {
			property_id,
			source: formType ? 14 : source,
			type: propertyType,
			financing_information: formType ? false : formData?.financing_information,
			is_terms_and_conditions_accepted: formData?.is_terms_and_conditions_accepted,
		};

		if (type == "lead") {
			contactInput.message = formType ? formType.toString() : formData?.message;
		} else {
			contactInput.date = formData?.date;
			contactInput.visit_type = formData?.visit_type;
		}

		if (formData?.isFromPaidTraffic) {
			contactInput.isFromPaidTraffic = formData?.isFromPaidTraffic;
		}

		if (utm && (utm.utm_campaign || utm.utm_content || utm.utm_medium || utm.utm_source)) {
			contactInput.utm_args = {};

			if (utm.utm_campaign) {
				contactInput.utm_args.utm_campaign = Array.isArray(utm.utm_campaign)
					? utm.utm_campaign[0]
					: utm.utm_campaign.toString();
			}
			if (utm.utm_content) {
				contactInput.utm_args.utm_content = Array.isArray(utm.utm_content)
					? utm.utm_content[0]
					: utm.utm_content.toString();
			}
			if (utm.utm_medium) {
				contactInput.utm_args.utm_medium = Array.isArray(utm.utm_medium)
					? utm.utm_medium[0]
					: utm.utm_medium.toString();
			}
			if (utm.utm_source) {
				contactInput.utm_args.utm_source = Array.isArray(utm.utm_source)
					? utm.utm_source[0]
					: utm.utm_source.toString();
			}
		}

		if (formData && formData.busca_por_ti) {
			const idealFilters = JSON.parse(localStorage.getItem("propertyIdealFilters"));

			if (idealFilters) {
				searchAlert.customForm(1, `Quiero que ${site_name} busque por mí`, idealFilters);
			}
		}

		if (isTransactional(listing?.highlight)) {
			setIDform(listing?.idForm);
			return Promise.resolve();
		} else {
			if (withoutLogin) {
				contactInput = {
					...contactInput,
					...extraFields,
					name: formData?.name || extraFields?.name,
					email: formData?.email || extraFields?.email,
					phone: formData?.phone || extraFields?.phone,
					message: formData?.message || extraFields?.message || `Hola, vi esta propiedad en ${site_name} y me interesa recibir más información. ¡Muchas gracias!`,
				};
			}
			return contactMutation({
				variables: {
					input: contactInput,
				},
			})
				.then(response => {
					if (type === "lead" && response?.data?.sendLead?.id) trackNpsLeadSent();

					if (onComplete) onComplete(formType);

					if (formType === 99)
						setIsSendedConsultation({ ...isSendedConsultation, wantedCall: true });
					else if (formType === 4)
						setIsSendedConsultation({ ...isSendedConsultation, askMoreInfo: true });
					else if (formData && formData.typeForm === 4)
						setIsSendedConsultation({
							...isSendedConsultation,
							sendConsultation: true,
						});
					else if (formData && formData.typeForm === 5)
						setIsSendedConsultation({ ...isSendedConsultation, coordinateVisit: true });

					return response;
				})
				.catch(error => {
					console.error(error);
				});
		}
	};

	const sendTransactionalContact = () => {
		const sendValues: valueFormDataType = {
			nombre: formData?.name,
			email: formData?.email,
			fecha: formData?.date,
			tel: formData?.phone,
			extra: makeExtra(formData),
		};
		return sendFormulario(sendValues, utm);
	};
	const sendContact = data => {
		const newFormData: FormData = {
			email: data.email,
			name: data.name,
			phone: data.phone,
			message: data?.message || `Hola, vi esta propiedad en ${site_name} y me interesa recibir más información. ¡Muchas gracias!`,
			financing_information: !!data.financing_information,
			date: data?.date
				? `${data.date.getFullYear()}-${data.date.getMonth() + 1}-${data.date.getDate()}`
				: null,
			visit_type: data?.visit_type == "INPERSON" ? VisitType.INPERSON : VisitType.VIRTUAL,
			isFromPaidTraffic: data?.isFromPaidTraffic,
			typeForm: data.isSendingConsultation ? 4 : data.isCoordinatingVisit ? 5 : 0,
			busca_por_ti: data.busca_por_ti,
			is_terms_and_conditions_accepted: data.fincatyc,
			isProject: listing?.isProject ?? false,
			projectId: id.toString(),
		};

		setFormData(newFormData);
	};

	const resetForm = () => {
		setFormComplete(false);
		setFormData(null);
	};

	const loading =
		contactLoading ||
		registerLoading ||
		updateLoading ||
		userExistsLoading ||
		userExistsCustomLoading;

	return {
		loading,
		error,
		formCompleted: formComplete,
		resetForm: resetForm,
		sendContact,
		disabledEmail: isLoggedIn,
		contactMutation,
		dataRegisterMutation,
		dataUserExist,
		updateUser,
		userExistsCustomQuery,
		registerMutation,
		doContactMutation,
		leadID,
		isSendedConsultation,
	};
};
